import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Table, Button, Badge, Form, Spinner, Offcanvas, Tabs, Tab } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import {
  getSuppliesReport,
  getSuppliesReportFull,
  getSuppliesReportTotals,
} from '../../services/Supplies/Supplies';
import { MySuppliesReport } from './RequestSuppliesReport';

const SuppliesReports = () => {
  // filtros
  const [status, setStatus] = useState('all'); // all | active | inactive
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // data
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // ui
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState('pdf');

  const [exportRows, setExportRows] = useState([]);
  const [exportTotals, setExportTotals] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    const t = (qInput || '').trim();
    const h = setTimeout(() => setQ(t), 350);
    return () => clearTimeout(h);
  }, [qInput]);

  const pageCount = useMemo(() => {
    return Math.ceil((Number(totalCount) || 0) / (Number(pageSize) || 10));
  }, [totalCount, pageSize]);

  const currentPageLabel = (Number(totalCount) || 0) === 0 ? 0 : page;
  const safePageCount = pageCount;

  const money = (n) => {
    const v = Number(n || 0);
    return `$${v.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const statusBadge = (isActive) => (
    <Badge bg={isActive ? 'success' : 'secondary'}>
      {isActive ? 'Activa' : 'Inactiva'}
    </Badge>
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [listResp, totalsResp] = await Promise.all([
          getSuppliesReport({ page, pageSize, q, status }),
          getSuppliesReportTotals({ q, status }),
        ]);

        if (cancelled) return;

        setRows(Array.isArray(listResp?.rows) ? listResp.rows : []);
        setTotalCount(Number(listResp?.count) || 0);
        setTotals(Array.isArray(totalsResp) ? totalsResp : []);
      } catch (e) {
        console.error('Error cargando reportes de consumibles', e);
        if (!cancelled) {
          setRows([]);
          setTotalCount(0);
          setTotals([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, q, status]);

  const downloadExcel = async () => {
    if (loading || isExporting) return;

    setIsExporting(true);
    try {
      const full = await getSuppliesReportFull({ q, status });
      const exportTotals = await getSuppliesReportTotals({ q, status });
      const worker = new Worker('/excelWorkerConsumables.js');

      worker.onmessage = (e) => {
        const { ok, wbout, error } = e.data || {};
        if (!ok) {
          console.error('Error en worker Excel:', error);
          alert('Ocurrió un error al generar el Excel.');
          setIsExporting(false);
          worker.terminate();
          return;
        }
        try {
          const today = new Date().toISOString().slice(0, 10);
          const blob = new Blob([wbout], { type: 'application/octet-stream' });
          saveAs(blob, `Reporte-Consumibles-${today}.xlsx`);
        } finally {
          setIsExporting(false);
          worker.terminate();
        }
      };

      worker.onerror = (err) => {
        console.error('Worker error:', err);
        alert('Ocurrió un error al generar el Excel.');
        setIsExporting(false);
        worker.terminate();
      };

      worker.postMessage({
        supplies: full,
        totals: exportTotals,
        meta: { q, status },
      });
    } catch (e) {
      console.error(e);
      alert('Ocurrió un error al generar el Excel.');
      setIsExporting(false);
    }
  };

  const onChangeStatus = (e) => {
    setStatus(e.target.value);
    setPage(1);
  };

  const onChangeQ = (e) => {
    setQInput(e.target.value);
    setPage(1);
  };

  const onChangePageSize = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const formatTotal = (t) => {
    // tu backend devuelve: { name, total_supplies, total_pieces }
    const v = Number(t?.total_pieces ?? 0);
    return v.toLocaleString('es-MX');
  };

  const loadExportData = async () => {
    setExportLoading(true);
    try {
      const [full, t] = await Promise.all([
        getSuppliesReportFull({ q, status }),
        getSuppliesReportTotals({ q, status }),
      ]);
      setExportRows(Array.isArray(full) ? full : []);
      setExportTotals(Array.isArray(t) ? t : []);
    } catch (e) {
      console.error('Error cargando export data', e);
      setExportRows([]);
      setExportTotals([]);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <h2 className="mb-0">Reportes de Consumibles</h2>

        <div className="d-flex gap-2">
          <Button variant="primary">Agregar</Button>

          <Button
            variant="outline-primary"
            onClick={() => { setPreviewOpen(true); loadExportData(); }}
            disabled={loading || totalCount === 0}
          >
            Visualizar PDF
          </Button>

          <Button
            variant="outline-success"
            onClick={downloadExcel}
            disabled={loading || !rows.length || isExporting}
          >
            {isExporting ? 'Generando Excel…' : 'Descargar Excel'}
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Row className="g-3 align-items-end mb-3">
        <Col md={3}>
          <Form.Label className="fw-bold">Estado</Form.Label>
          <Form.Select value={status} onChange={onChangeStatus}>
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </Form.Select>
        </Col>

        <Col md={6}>
          <Form.Label className="fw-bold">Buscar</Form.Label>
          <Form.Control
            value={qInput}
            onChange={onChangeQ}
            placeholder="Buscar por clave, nombre, proveedor..."
          />
        </Col>

        <Col md={3}>
          <Form.Label className="fw-bold">Por página</Form.Label>
          <Form.Select value={pageSize} onChange={onChangePageSize}>
            {[10, 20, 30, 50].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Totales */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="fw-bold">Totales (por proveedor)</div>
      </div>

      {totals.length > 0 && (
        <Row className="g-2 mb-3">
          {totals.map((t, idx) => (
            <Col key={`${t.name}-${idx}`} xs={12} md={4} lg={3}>
              <div className="border rounded p-2 bg-white d-flex justify-content-between align-items-center">
                <div style={{ fontSize: 13, textTransform: 'capitalize' }}>{t.name}</div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{formatTotal(t)}</div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {/* Tabla */}
      <div className="table-rounded-wrapper">
        <div className="table-responsive">
          <Table bordered hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Clave</th>
                <th>Nombre</th>
                <th>Lote</th>
                <th>Pzas/lote</th>
                <th>Pzas disp.</th>
                <th>Costo/lote</th>
                <th>Unidad</th>
                <th>Capacidad</th>
                <th>Proveedor</th>
                <th>Costo/unidad</th>
                <th>Precio/ml</th>
                <th>Estado</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={12} className="text-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-5">Sin registros</td>
                </tr>
              ) : (
                rows.map((s) => (
                  <tr key={s.id}>
                    <td>{s.code || '—'}</td>
                    <td className="col-left">{s.product_name || '—'}</td>
                    <td>{Number(s.lot || 0).toLocaleString('es-MX')}</td>
                    <td>{Number(s.pieces_per_lot || 0).toLocaleString('es-MX')}</td>
                    <td>{Number(s.available_pieces || 0).toLocaleString('es-MX')}</td>
                    <td>{money(s.lot_cost)}</td>
                    <td>{s.unitofmeasure_label || s.unitOfMeasureLabel || s.unitofmeasure_id || '—'}</td>
                    <td>{s.capacity_dimensions || '—'}</td>
                    <td>{s.supplier || '—'}</td>
                    <td>{money(s.unit_cost)}</td>
                    <td>{money(s.price_per_ml)}</td>
                    <td>{statusBadge(!!s.isActive)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* VISUALIZACION */}
      <Offcanvas show={previewOpen} onHide={() => setPreviewOpen(false)} placement="end" style={{ width: 900 }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Vista previa del reporte</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <div className="d-flex gap-2 mb-3">
            <PDFDownloadLink
              document={<MySuppliesReport data={exportRows} totals={exportTotals} meta={{ q, status }} />}
              fileName={`Reporte-Consumibles-${new Date().toISOString().slice(0, 10)}.pdf`}
            >
              {({ loading: pdfLoading }) => (
                <Button variant="outline-primary" disabled={pdfLoading || exportLoading}>
                  {(pdfLoading || exportLoading) ? 'Generando PDF…' : 'Descargar PDF'}
                </Button>
              )}
            </PDFDownloadLink>

            <Button
              variant="outline-success"
              onClick={downloadExcel}
              disabled={loading || !rows.length || isExporting}
            >
              {isExporting ? 'Generando Excel…' : 'Descargar Excel'}
            </Button>
          </div>

          <Tabs activeKey={previewTab} onSelect={(k) => setPreviewTab(k)} className="mb-3">
            <Tab eventKey="pdf" title="PDF">
              <div style={{ height: '70vh', border: '1px solid #eee' }}>
                {exportLoading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando datos para el reporte...
                  </div>
                ) : (
                  <PDFViewer style={{ width: '100%', height: '100%' }}>
                    <MySuppliesReport data={exportRows} totals={exportTotals} meta={{ q, status }} />
                  </PDFViewer>
                )}
              </div>
            </Tab>

            <Tab eventKey="excel" title="Excel">
              <div className="table-responsive">
                <Table bordered hover size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>Clave</th>
                      <th>Nombre</th>
                      <th>Lote</th>
                      <th>Pzas/lote</th>
                      <th>Pzas disp.</th>
                      <th>Costo/lote</th>
                      <th>Unidad</th>
                      <th>Capacidad</th>
                      <th>Proveedor</th>
                      <th>Costo/unidad</th>
                      <th>Precio/ml</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exportRows.map((s) => (
                      <tr key={s.id}>
                        <td>{s.code || '—'}</td>
                        <td>{s.product_name || '—'}</td>
                        <td>{Number(s.lot || 0).toLocaleString('es-MX')}</td>
                        <td>{Number(s.pieces_per_lot || 0).toLocaleString('es-MX')}</td>
                        <td>{Number(s.available_pieces || 0).toLocaleString('es-MX')}</td>
                        <td>{money(s.lot_cost)}</td>
                        <td>{s.unitofmeasure_label || s.unitOfMeasureLabel || s.unitofmeasure_id || '—'}</td>
                        <td>{s.capacity_dimensions || '—'}</td>
                        <td>{s.supplier || '—'}</td>
                        <td>{money(s.unit_cost)}</td>
                        <td>{money(s.price_per_ml)}</td>
                        <td>{!!s.isActive ? 'Activa' : 'Inactiva'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Tab>
          </Tabs>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Paginación */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Página {currentPageLabel} de {safePageCount} ({Number(totalCount) || 0} registros)
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || safePageCount === 0}
          >
            Anterior
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage((p) => Math.min(safePageCount, p + 1))}
            disabled={safePageCount === 0 || page >= safePageCount}
          >
            Siguiente
          </Button>
        </div>
      </div>
    </>
  );
};

export default SuppliesReports;